/**
 * Configuración → Series de documento (`/configuracion/series-documento`).
 *
 * Puerto de `Document_series` + `views/document_series/*`: cada serie pertenece a
 * un tipo de documento y a un tipo de operación, y puede llevar el identificador
 * que usa el proveedor de facturación electrónica (`apiSeriesId`).
 *
 * La vista anterior tenía el formulario en una página aparte (`create.php` /
 * `edit.php`); aquí se usa el diálogo del panel, con los mismos campos,
 * incluido el interruptor *usar identificador de la API*.
 *
 * Diferencias anotadas: la serie y el `apiSeriesId` son únicos (409), el estado
 * no se fuerza al guardar (antes, editar reactivaba) y no hay borrado — se activa
 * o desactiva. `numeracion_actual` se muestra pero no se edita: la lleva el
 * sistema.
 */

import { ArrowLeft, Barcode, Pencil, Plus, Power, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber } from '@/lib/utils';

import { useMutarSeriesDocumento, useSeriesDocumento, useTiposDocumento } from '../catalogos.api';
import type { OperacionSerie, SerieDocumento, TipoDocumento } from '../catalogos.types';

/** Etiquetas del `enum document_series.operationType`. */
const OPERACIONES: readonly { valor: OperacionSerie; etiqueta: string }[] = [
  { valor: 'puntoventa', etiqueta: 'Punto de venta' },
  { valor: 'market', etiqueta: 'Market' },
  { valor: 'masivo', etiqueta: 'Masivo' },
  { valor: 'administrativo', etiqueta: 'Administrativo' },
  { valor: 'autoservicio', etiqueta: 'Autoservicio' },
];

const ETIQUETA_OPERACION = Object.fromEntries(
  OPERACIONES.map((operacion) => [operacion.valor, operacion.etiqueta]),
) as Record<OperacionSerie, string>;

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Alta/edición de una serie. */
function SerieDialogo({
  serie,
  tipos,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  serie: SerieDocumento | null;
  tipos: TipoDocumento[];
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: {
    tipo_id: number;
    serie: string;
    operacion: OperacionSerie;
    api_serie: string;
    activo?: boolean;
  }) => void;
  onCerrar: () => void;
}) {
  const [tipoId, setTipoId] = useState(serie?.tipo_id ?? tipos[0]?.id ?? 0);
  const [codigo, setCodigo] = useState(serie?.serie ?? '');
  const [operacion, setOperacion] = useState<OperacionSerie>(serie?.operacion ?? 'puntoventa');
  const [usaApi, setUsaApi] = useState(Boolean(serie?.api_serie));
  const [apiSerie, setApiSerie] = useState(serie?.api_serie ?? '');
  const [activo, setActivo] = useState(serie?.activo ?? true);

  const valido = tipoId > 0 && codigo.trim().length > 0 && (!usaApi || apiSerie.trim().length > 0);

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={serie ? `Editar serie ${serie.serie}` : 'Nueva serie de documento'}
      description="La serie identifica el correlativo que se usa al emitir cada comprobante."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                tipo_id: tipoId,
                serie: codigo.trim().toUpperCase(),
                operacion,
                api_serie: usaApi ? apiSerie.trim() : '',
                ...(serie ? { activo } : {}),
              })
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="serie-tipo" className="text-sm font-medium text-ink-700">
            Tipo de documento
          </label>
          <Select
            id="serie-tipo"
            className={CONTROL}
            value={String(tipoId)}
            onChange={(evento) => setTipoId(Number(evento.target.value))}
          >
            {tipos.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.codigo} · {tipo.nombre}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Código de serie"
            value={codigo}
            maxLength={10}
            autoFocus
            placeholder="Ej. FE01"
            className="uppercase"
            onChange={(evento) => setCodigo(evento.target.value.toUpperCase())}
          />

          <div className="flex flex-col gap-1.5">
            <label htmlFor="serie-operacion" className="text-sm font-medium text-ink-700">
              Tipo de operación
            </label>
            <Select
              id="serie-operacion"
              className={CONTROL}
              value={operacion}
              onChange={(evento) => setOperacion(evento.target.value as OperacionSerie)}
            >
              {OPERACIONES.map((opcion) => (
                <option key={opcion.valor} value={opcion.valor}>
                  {opcion.etiqueta}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-control border border-ink-100 bg-ink-50/50 p-3">
          <Checkbox
            label="Usar identificador de la API"
            hint="Para el proveedor de facturación electrónica (debe ser único)"
            checked={usaApi}
            onChange={(evento) => setUsaApi(evento.target.checked)}
          />
          {usaApi ? (
            <Input
              label="Identificador de la API"
              value={apiSerie}
              maxLength={50}
              placeholder="Ej. F001-2025"
              onChange={(evento) => setApiSerie(evento.target.value)}
            />
          ) : null}
        </div>

        {serie ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-ink-700">Estado</span>
            <Checkbox
              label="Serie activa"
              checked={activo}
              onChange={(evento) => setActivo(evento.target.checked)}
            />
            <span className="ml-auto text-xs text-ink-500">
              Numeración actual: {formatNumber(serie.numeracion_actual)}
            </span>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

export function SeriesDocumentoPage() {
  useDocumentTitle('Configuración · Series de documento');

  const series = useSeriesDocumento();
  const tipos = useTiposDocumento();
  const mutaciones = useMutarSeriesDocumento();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; serie: SerieDocumento | null }>({
    abierto: false,
    serie: null,
  });
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = series.error instanceof ApiError && series.error.status === 403;
  const lista = series.data?.series ?? [];
  const listaTipos = tipos.data?.tipos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Barcode className="size-6 text-brand-500" aria-hidden="true" />
          Series de documento
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Correlativos por tipo de documento y de operación. Los tipos se administran en{' '}
          <Link to="/configuracion/tipos-documento" className="font-medium text-brand-600 hover:underline">
            Tipos de documento
          </Link>
          .
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso || listaTipos.length === 0}
        title={listaTipos.length === 0 ? 'Primero registra un tipo de documento' : undefined}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, serie: null });
        }}
      >
        Nueva serie
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: {
    tipo_id: number;
    serie: string;
    operacion: OperacionSerie;
    api_serie: string;
    activo?: boolean;
  }) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, serie: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar la serie.')),
    };

    if (dialogo.serie) {
      mutaciones.actualizar.mutate({ id: dialogo.serie.id, payload }, alGuardar);
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = (serie: SerieDocumento) => {
    mutaciones.cambiarEstado.mutate(
      { id: serie.id, activo: !serie.activo },
      {
        onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {series.isLoading ? (
        <Skeleton className="h-80 w-full" />
      ) : series.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer las series">
          {mensajeDe(series.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Listado de series</CardTitle>
            <Badge variant="neutral">{series.data?.total ?? 0} registradas</Badge>
          </CardHeader>
          <CardContent>
            {lista.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-500">
                Todavía no hay series registradas.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                      <th className="w-24 pb-2">Serie</th>
                      <th className="w-48 pb-2">Tipo de documento</th>
                      <th className="w-36 pb-2">Operación</th>
                      <th className="pb-2">Identificador API</th>
                      <th className="w-28 pb-2 text-right">Numeración</th>
                      <th className="w-24 pb-2">Estado</th>
                      <th className="w-28 pb-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((serie) => (
                      <tr key={serie.id} className="border-t border-ink-100">
                        <td className="py-3">
                          <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                            {serie.serie}
                          </span>
                        </td>
                        <td className="py-3 text-ink-800">
                          <span className="text-ink-500">{serie.tipo_codigo} · </span>
                          {serie.tipo_nombre}
                        </td>
                        <td className="py-3 text-ink-600">{ETIQUETA_OPERACION[serie.operacion]}</td>
                        <td className="py-3 font-mono text-xs text-ink-500">
                          {serie.api_serie || '—'}
                        </td>
                        <td className="py-3 text-right text-ink-700">
                          {formatNumber(serie.numeracion_actual)}
                        </td>
                        <td className="py-3">
                          <Badge variant={serie.activo ? 'success' : 'neutral'}>
                            {serie.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Editar serie ${serie.serie}`}
                              title="Editar"
                              onClick={() => {
                                setErrorDialogo(null);
                                setDialogo({ abierto: true, serie });
                              }}
                            >
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={
                                serie.activo ? `Desactivar serie ${serie.serie}` : `Activar serie ${serie.serie}`
                              }
                              title={serie.activo ? 'Desactivar' : 'Activar'}
                              loading={mutaciones.cambiarEstado.isPending}
                              onClick={() => cambiarEstado(serie)}
                            >
                              <Power
                                className={`size-4 ${serie.activo ? 'text-emerald-600' : 'text-ink-400'}`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Alert variant="info" className="mt-4">
              La <strong>numeración</strong> es informativa: la lleva el sistema al emitir cada
              comprobante. Las series no se eliminan, se desactivan.
            </Alert>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <SerieDialogo
          key={`serie-${dialogo.serie?.id ?? 'nueva'}`}
          serie={dialogo.serie}
          tipos={listaTipos}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, serie: null })}
        />
      ) : null}
    </div>
  );
}
